import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingOverlay, Stack } from '@mantine/core'
import * as yup from 'yup'

import { AttendeeCard } from '@/components/Event/NewScript/components/common/AttendeeCard/AttendeeCard'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import {
  ActionContainer,
  ActionTitle,
  TimelineAction,
} from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { EventCancelledCard } from '@/components/Event/NewScript/components/Review/EventCancelledCard/EventCancelledCard'
import { EventResultData } from '@/components/Event/NewScript/components/Review/EventCancelledCard/EventCancelledCardEdit'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import {
  EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED,
  EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED,
} from '@/components/Event/NewScript/schemas'
import { cancelledReviewStepSchema } from '@/components/Event/NewScript/schemas/reviewStepSchema'
import { omitPerAttendeeErrors } from '@/components/Event/NewScript/utils/attendees'
import { getWitnessEventTitle } from '@/components/Event/NewScript/utils/event'
import { AttendeeRole, DepositionResult, ScriptStep } from '@/constants'
import { Event, EventScriptDataObject, ScriptData } from '@/types'

type ReviewStepType = yup.InferType<typeof cancelledReviewStepSchema>

type EventCancelledReviewFormProps = {
  event: Event
  refetchEvent?: () => Promise<unknown>
  state: EventScriptDataObject
  witnessIndex: number
}

export const EventCancelledReviewForm = ({
  event,
  state,
  witnessIndex,
  ...rest
}: EventCancelledReviewFormProps) => {
  const { state: globalState, actions, isLoading } = useScriptState()
  const { current_step, events } = globalState as ScriptData
  const { first_skipped_step } = state
  const { completeAction, isActionCompleted } = useStepActions(
    current_step,
    event,
  )

  const methods = useForm({
    mode: 'all',
    context: {
      [EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED]: false,
      [EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED]: false,
    },
    resolver: yupResolver(cancelledReviewStepSchema),
    defaultValues: {
      attendees: state.attendees,
      result: state.result,
      notes: state.notes,
      cna_declared_at: state.cna_declared_at,
      last_timestamp: state.last_timestamp,
      reason_for_cna: state.reason_for_cna ?? undefined,
      other_reason: state.other_reason,
      no_show_is_on_record: true,
      no_show_is_off_record: true,
    },
  })

  const syncCurrentEventAttendees = (nextAttendees: typeof state.attendees) => {
    actions.updateGlobalState({
      events: events.map((event, index) => ({
        ...event,
        attendees: index === witnessIndex ? nextAttendees : event.attendees,
      })),
    })
  }

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (
        name &&
        [
          'result',
          'notes',
          'cna_declared_at',
          'last_timestamp',
          'reason_for_cna',
          'other_reason',
        ].includes(name)
      ) {
        return actions.updateGlobalState({
          events: events.map((event, index) => ({
            ...event,
            result: index === witnessIndex ? value.result : event.result,
            notes: index === witnessIndex ? value.notes : event.notes,
            cna_declared_at:
              index === witnessIndex
                ? value.cna_declared_at
                : event.cna_declared_at,
            last_timestamp:
              index === witnessIndex
                ? value.last_timestamp
                : event.last_timestamp,
            reason_for_cna:
              index === witnessIndex
                ? value.reason_for_cna
                : event.reason_for_cna,
            other_reason:
              index === witnessIndex ? value.other_reason : event.other_reason,
          })),
        })
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, state, actions])

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (
        name &&
        ['reason_for_cna', 'other_reason', 'notes', 'result'].includes(name)
      ) {
        if (value.result === DepositionResult.CNA) {
          actions.unskipStepAction({
            currentEventIndex: witnessIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: [
              {
                step: ScriptStep.CLOSING,
                key: 'state_the_cna',
              },
              {
                step: ScriptStep.CLOSING,
                key: 'select_reason_for_cna',
              },
            ],
          })
        } else {
          actions.skipStepAction({
            currentEventIndex: witnessIndex,
            result: value.result,
            notes: value.notes,
            reasonForCNA: value.reason_for_cna,
            otherReason: value.other_reason,
            stepAndActionKeys: [
              {
                step: ScriptStep.CLOSING,
                key: 'state_the_cna',
              },
              {
                step: ScriptStep.CLOSING,
                key: 'select_reason_for_cna',
              },
            ],
          })
        }
      }
    })

    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, state, actions])

  const validateAndCompleteAction = async (
    validation_keys: Array<keyof ReviewStepType>,
    action_key: string,
  ) => {
    const results: Promise<boolean>[] = []

    validation_keys.forEach(key => {
      results.push(methods.trigger(key))
    })

    Promise.all(results).then(res => {
      res.every(isValid => isValid) && completeAction(action_key)
    })
  }

  if (isLoading) {
    return <LoadingOverlay visible />
  }

  const witnessName = state.attendees.find(
    a => a.role === AttendeeRole.WITNESS,
  )?.name

  return (
    <TimelineAction
      {...rest}
      actionKey={`review-${witnessIndex}`}
      title={getWitnessEventTitle(event, witnessName)}
      onComplete={key =>
        validateAndCompleteAction(
          [
            'result',
            'notes',
            'reason_for_cna',
            'other_reason',
            'cna_declared_at',
            'attendees',
          ],
          key,
        )
      }
      completed={isActionCompleted(`review-${witnessIndex}`)}
    >
      <FormProvider {...methods}>
        <EventCancelledCard
          title="Deposition ended"
          event={event}
          value={{
            result: state.result,
            notes: state.notes,
            reason_for_cna: state.reason_for_cna,
            other_reason: state.other_reason,
            cna_declared_at: state.cna_declared_at,
            last_timestamp: state.last_timestamp,
          }}
          onSubmitClick={async data => {
            Object.entries(data).forEach(([key, value]) =>
              methods.setValue(key as keyof EventResultData, value),
            )
            await methods.trigger([
              'result',
              'notes',
              'reason_for_cna',
              'last_timestamp',
              'other_reason',
              'cna_declared_at',
            ])
          }}
          canceledFromStep={first_skipped_step}
        />
        <ActionContainer outer>
          <Stack>
            <ActionTitle>Attendee information</ActionTitle>
            {state.attendees.map((attendee, index) => (
              <AttendeeCard
                onSubmitClick={submittedAttendee => {
                  const formAttendees = methods.getValues('attendees')
                  const nextAttendees = formAttendees.map(formAttendee =>
                    formAttendee.attendee_id === submittedAttendee.attendee_id
                      ? submittedAttendee
                      : formAttendee,
                  )

                  methods.setValue(`attendees.${index}`, submittedAttendee)
                  syncCurrentEventAttendees(nextAttendees)
                }}
                event={event}
                key={attendee.attendee_id}
                attendee={attendee}
                namePrefix={`attendees.${index}`}
                allAttendees={state.attendees}
              />
            ))}
          </Stack>
        </ActionContainer>
        {methods.formState.errors && (
          <FormErrors
            errors={omitPerAttendeeErrors(methods.formState.errors)}
          />
        )}
      </FormProvider>
    </TimelineAction>
  )
}
